import { templateVariablesMap } from "@pathwright/blocks-engine"
import LazyQuillEditor from "@pathwright/ui/src/components/quill/LazyQuillEditor"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
// import { getCustomColors } from "../editor/BlockStyles/utils"
import classNames from "classnames"
import debounce from "lodash/debounce"
import type * as QuillTypes from "quill"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useBlockEditorContext } from "../editor/BlockEditorContext"
import { isEmptyHTML } from "../utils"
import BlockText from "./BlockText"
import useQuillSlashCommands, {
  QuillSlashCommandKey
} from "./useQuillSlashCommands"

type CustomColors = {
  color: string
  backgroundColor: string
}

// TEMP FIX: importing getCustomColors results in circulum import error.
const lsKey = "customBlocksColors"
const getCustomColors = () =>
  JSON.parse(window.localStorage.getItem(lsKey) || "{}") as CustomColors

const getBlocksColorsForQuill = () => {
  const { color: customColor, backgroundColor: customBackgroundColor } = {
    ...(getCustomColors() || {})
  }

  const customColors: string[] = []

  if (customColor) customColors.push(customColor)
  if (customBackgroundColor) customColors.push(customBackgroundColor)

  return [{ color: customColors }, { background: customColors }]
}

type OnChange = (
  html: string,
  delta: QuillTypes.DeltaStatic,
  source: QuillTypes.Sources
) => void

type BlocksQuillEditorProps = {
  // Required props
  placeholder: string
  html: string
  onChange: OnChange

  // Optional props from QuillEditor
  createCustomToolbar?: (props: {
    getMergedToolbarConfig: (config?: any) => any
  }) => React.ReactNode
  controlled?: boolean
  // Ignore the isActive optimization
  // and make the editor always editable
  alwaysEditable?: boolean
  style?: React.CSSProperties
  LoadingComponent?: React.ComponentType<any>

  // QuillEditor props that can be passed through
  toolbar?: string | number
  autofocus?: boolean | Pick<FocusOptions, "preventScroll">
  readOnly?: boolean
  isEditable?: boolean
  renderNonEditable?: (props: {
    html: string
    placeholder?: string
  }) => React.ReactNode
  onKeyUp?: React.KeyboardEventHandler
  onKeyDown?: React.KeyboardEventHandler
  onBlur?: (
    previousRange: QuillTypes.RangeStatic,
    source: QuillTypes.Sources,
    editor: any
  ) => void
  onFocus?: (
    range: QuillTypes.RangeStatic,
    source: QuillTypes.Sources,
    editor: any
  ) => void
  onChangeSelection?: (
    range: QuillTypes.RangeStatic,
    source: QuillTypes.Sources,
    editor: any
  ) => void
  theme?: "bubble" | "snow"
  bounds?: string
  contentType?: "text"
  useAdvancedColorPicker?: boolean
  onDelete?: () => void
  onEnter?: (event: KeyboardEvent) => boolean | void
  className?: string
  inverted?: boolean
  /** Delimiter-scoped groups: [{ delimiters, variables }] */
  variables?: Array<{ delimiters: string[]; variables: Record<string, any> }>
  slashCommandKeys?: QuillSlashCommandKey[]
  // Allow any additional props that LazyQuillEditor might accept
  [key: string]: any
}

const BlocksQuillEditor = ({
  LoadingComponent,
  placeholder,
  createCustomToolbar,
  alwaysEditable = false,
  style: styleProp = {},
  onChange,
  html: htmlProp,
  controlled,
  className,
  variables: variablesProp,
  slashCommandKeys,
  ...rest
}: BlocksQuillEditorProps) => {
  const { t } = useTranslate()
  const { isActive } = useBlockEditorContext()

  // If isActive is undefined, then this QuillEditor
  // is probably being used outside of a regular editor block
  const isEditable = alwaysEditable || isActive !== false

  const [slashCommands, mediaManager] = useQuillSlashCommands({
    keys: slashCommandKeys
  })

  const variables = useMemo(() => {
    if (variablesProp) return variablesProp

    const groups: Array<{
      delimiters: string[]
      variables: Record<string, any>
    }> = [{ delimiters: ["at", "bracket"], variables: templateVariablesMap }]

    if (slashCommands) {
      groups.push({ delimiters: ["slash"], variables: slashCommands })
    }

    return groups
  }, [variablesProp, slashCommands])

  // Debounce onChange to reduce reducer dispatches during typing
  const DEBOUNCE_DELAY_MS = 500
  const onChangeRef = useRef(onChange)
  const isAwaitingPropSyncRef = useRef(false)
  const lastDispatchedHtmlRef = useRef(htmlProp || "")
  const previousHtmlPropRef = useRef(htmlProp)

  // Keep ref in sync with latest onChange
  onChangeRef.current = useCallback<OnChange>(
    (html, delta, source) => {
      lastDispatchedHtmlRef.current = html
      return onChange?.(html, delta, source)
    },
    [onChange]
  )

  // For controlled mode: maintain local state that updates immediately
  // while debouncing parent updates
  const [localHtml, setLocalHtml] = useState(() => htmlProp || "")

  // Keep local input authoritative until the parent value catches up.
  // This prevents stale controlled props from briefly overwriting recent typing
  // while debounced/networked saves are still settling.
  const controlledHtml =
    !isAwaitingPropSyncRef.current && htmlProp != null ? htmlProp : localHtml

  // Sync local state with prop once the parent has caught up to the latest
  // locally dispatched value, or immediately when there are no local edits
  // waiting to be reflected upstream.
  useEffect(() => {
    if (!controlled) return

    const didPropChange = htmlProp !== previousHtmlPropRef.current
    previousHtmlPropRef.current = htmlProp

    // The controlled prop now matches the last value we
    // handed to the parent, so it's safe to trust props again. Only clear the
    // guard after the prop has actually changed; otherwise we can mistake the
    // old prop value for a completed catch-up and briefly revert the editor.
    if (didPropChange && htmlProp === lastDispatchedHtmlRef.current) {
      isAwaitingPropSyncRef.current = false
    }

    if (isAwaitingPropSyncRef.current) return

    const nextHtml = htmlProp || ""
    if (nextHtml !== localHtml) {
      setLocalHtml(nextHtml)
    }
  }, [controlled, htmlProp, localHtml])

  // Create debounced onChange function
  const debouncedOnChange = useMemo(() => {
    if (!onChange) return undefined

    return debounce<OnChange>((html, delta, source) => {
      onChangeRef.current(html, delta, source)
    }, DEBOUNCE_DELAY_MS)
  }, []) // Empty deps - function is stable, uses ref for latest onChange

  // Helper to check if HTML content is effectively empty
  const isEmptyHtml = (value: string | undefined) =>
    !value || value === "" || value === "<p><br></p>"

  // Handle onChange: update local state immediately, debounce parent update
  const handleChange: OnChange = (html, delta, source) => {
    const previousValue = controlled ? localHtml : htmlProp
    const wasEmpty = isEmptyHtml(previousValue)
    const isNowNonEmpty = !isEmptyHtml(html)

    if (controlled) {
      // Update local state immediately for responsive UI
      setLocalHtml(html)
    }

    // Fire immediately on empty→non-empty transition (structural change, not typing)
    // This enables patterns like auto-generating list items on first character
    if (wasEmpty && isNowNonEmpty) {
      onChangeRef.current(html, delta, source)
      return
    }

    // Debounce parent update
    if (debouncedOnChange) {
      if (controlled) {
        // Protect local text until the controlled prop advances to the value we
        // actually dispatched upward from the debounced callback.
        isAwaitingPropSyncRef.current = true
      }
      debouncedOnChange(html, delta, source)
    }
  }

  // Flush pending changes on unmount
  useEffect(() => {
    return () => {
      if (debouncedOnChange) {
        debouncedOnChange.flush()
      }
    }
  }, [debouncedOnChange])

  const style = {
    ...styleProp
    // backgroundColor: "rgba(0,0,0,0.03)" // DEBUG only
  }

  const placeholderStyle = {
    color: "PRIMARY_GRAY",
    opacity: 0.6,
    fontStyle: "italic",
    fontSize: "18px",
    ...style
  }

  // For controlled mode, use controlledHtml (which syncs with prop when no pending changes);
  // for uncontrolled, use prop directly
  const htmlValue = controlled ? controlledHtml : htmlProp
  const emptyHTML = isEmptyHTML(htmlValue)

  return (
    <>
      {mediaManager}
      <LazyQuillEditor
        {...rest}
        placeholder={placeholder || t("blocks.text_editor.placeholder")}
        variables={variables}
        contentType="text"
        createCustomToolbar={
          createCustomToolbar
            ? createCustomToolbar
            : ({
                getMergedToolbarConfig
              }: {
                getMergedToolbarConfig: (config: any) => any
              }) =>
                getMergedToolbarConfig({
                  config: getBlocksColorsForQuill()
                })
        }
        isEditable={isEditable}
        renderNonEditable={({ html }: { html: string }) => (
          <BlockText
            html={!emptyHTML ? html : placeholder}
            style={emptyHTML && placeholder ? placeholderStyle : style}
            className={classNames(
              `${
                placeholder && isEditable
                  ? "quill-editor-noneditable--placeholder"
                  : ""
              } quill-editor-noneditable`,
              className
            )}
          />
        )}
        useAdvancedColorPicker
        LoadingComponent={
          LoadingComponent ? () => <LoadingComponent /> : () => <></>
        }
        style={styleProp}
        html={htmlValue}
        controlled={controlled}
        onChange={handleChange}
      />
    </>
  )
}

BlocksQuillEditor.displayName = "BlocksQuillEditor"

export default BlocksQuillEditor
